import { css, cx } from '@emotion/css';
import { Position, useConnection, useInternalNode } from '@xyflow/react';

import { Handle } from '@/components/diagrams/components/Handle';
import { COLORS } from '@/styles/tokens/colors';

/** While the user drags a line, the handles within this distance of its end grow. */
const NEAR_DISTANCE = 64;

const styles = {
  // The nested selector beats the default React Flow handle styles.
  handle: css({
    '&.react-flow__handle': {
      width: 16,
      height: 16,
      minWidth: 0,
      minHeight: 0,
      border: 'none',
      background: 'transparent',
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      zIndex: 3,
      cursor: 'crosshair',
      opacity: 0,
      transition: 'opacity 120ms ease-out',
    },
    '& > span': {
      display: 'block',
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: COLORS.PRIMITIVES.WHITE,
      border: `1.5px solid ${COLORS.GRAY[400]}`,
      pointerEvents: 'none',
      transition:
        'transform 140ms ease-out, background 140ms, border-color 140ms, box-shadow 140ms',
    },
    '&:hover > span': {
      transform: 'scale(1.35)',
      borderColor: COLORS.BLUE[600],
    },
    // The handles show when the user points to the node or selects it.
    '.react-flow__node:hover &.react-flow__handle, .react-flow__node.selected &.react-flow__handle':
      { opacity: 1 },
    '@media (prefers-reduced-motion: reduce)': {
      '&, & > span': { transition: 'none' },
    },
  }),
  visible: css({ '&.react-flow__handle': { opacity: 1 } }),
  faint: css({ '&.react-flow__handle': { opacity: 0.5 } }),
  near: css({
    '& > span': { transform: 'scale(1.35)', borderColor: COLORS.BLUE[600] },
  }),
  target: css({
    '& > span': {
      transform: 'scale(1.8)',
      background: COLORS.BLUE[600],
      borderColor: COLORS.BLUE[600],
      boxShadow: `0 0 0 4px ${COLORS.BLUE[100]}`,
    },
  }),
};

/** The position of each side handle as a fraction of the node width and height. */
const SIDE_FRACTION: Record<Position, [number, number]> = {
  [Position.Top]: [0.5, 0],
  [Position.Right]: [1, 0.5],
  [Position.Bottom]: [0.5, 1],
  [Position.Left]: [0, 0.5],
};

/**
 * The state of a handle while the user drags a line:
 * - `idle`: no drag.
 * - `connecting`: a drag, with the line end far from the handle.
 * - `near`: the line end is near the handle.
 * - `target`: the line snaps to the handle.
 */
type HandleState = 'idle' | 'connecting' | 'near' | 'target';

function SideHandle({
  nodeId,
  side,
  x,
  y,
}: {
  nodeId: string;
  side: Position;
  /** The center of the handle in canvas pixels. */
  x: number;
  y: number;
}) {
  // A handle reads only its own state, so a drag frame renders only the
  // handles whose state changes.
  const state = useConnection((connection): HandleState => {
    if (!connection.inProgress) return 'idle';
    if (
      connection.toHandle?.nodeId === nodeId &&
      connection.toHandle.id === side
    ) {
      return 'target';
    }
    const distance = Math.hypot(connection.to.x - x, connection.to.y - y);
    return distance < NEAR_DISTANCE ? 'near' : 'connecting';
  });
  return (
    <Handle
      id={side}
      type="source"
      position={side}
      className={cx(
        styles.handle,
        state === 'connecting' && styles.faint,
        (state === 'near' || state === 'target') && styles.visible,
        state === 'near' && styles.near,
        state === 'target' && styles.target
      )}
    >
      <span />
    </Handle>
  );
}

/**
 * One connection handle on each side of a node. The handle ID is its side.
 * The handles show when the user points to the node. While the user drags a
 * line, the handles near the line end grow, and the handle that the line
 * snaps to fills in.
 *
 * Each handle is a `source` handle. Use them in a flow with
 * `connectionMode={ConnectionMode.Loose}`, so that a line can end at any
 * handle.
 */
export function ConnectionHandles({ nodeId }: { nodeId: string }) {
  const node = useInternalNode(nodeId);
  if (!node) return null;
  const { x, y } = node.internals.positionAbsolute;
  const width = node.measured.width ?? 0;
  const height = node.measured.height ?? 0;
  return Object.values(Position).map((side) => {
    const [fractionX, fractionY] = SIDE_FRACTION[side];
    return (
      <SideHandle
        key={side}
        nodeId={nodeId}
        side={side}
        x={x + width * fractionX}
        y={y + height * fractionY}
      />
    );
  });
}
